/**
 * liveChannel — Supabase Realtime that heals itself, and tells you when it can't.
 *
 * `channel.subscribe()` on its own is a one-shot hope. If the socket never comes
 * up, drops on a laptop sleep, dies on a network switch, or the JWT expires under
 * it, the channel goes quiet **permanently** — no error, no retry, nothing in the
 * UI. That is why notifications "only arrive after a reload": the reload was
 * rebuilding the socket, not fetching newer data.
 *
 * This wraps a subscription with the four things it was missing:
 *
 *   1. STATUS — `SUBSCRIBED` / `CHANNEL_ERROR` / `TIMED_OUT` / `CLOSED` is read,
 *      not ignored.
 *   2. RETRY — a dead channel is torn down and re-opened with exponential backoff
 *      (1s → 30s, jittered), forever, instead of staying dead.
 *   3. WAKE — coming back to the tab, or the network returning, retries at once
 *      and resets the backoff. Sleeping a laptop is the common case.
 *   4. TRUTH — the health of every channel is published, so the UI can say
 *      "reconnecting" and the data layer can poll harder while the socket is down.
 *
 * Handlers live in a ref, so re-rendering with new callbacks does NOT tear the
 * socket down and build a new one — only a change of `key` does.
 *
 * @since 3.4.0
 */
import { create } from 'zustand';
import { useEffect, useRef } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/db/supabase';

export type ChannelHealth = 'connecting' | 'live' | 'down';

interface LiveHealthState {
    channels: Record<string, ChannelHealth>;
    setHealth: (key: string, health: ChannelHealth) => void;
    dropChannel: (key: string) => void;
}

export const useLiveHealthStore = create<LiveHealthState>((set) => ({
    channels: {},
    setHealth: (key, health) => set((s) => (
        s.channels[key] === health ? s : { channels: { ...s.channels, [key]: health } }
    )),
    dropChannel: (key) => set((s) => {
        if (!(key in s.channels)) return s;
        const next = { ...s.channels };
        delete next[key];
        return { channels: next };
    }),
}));

// ── Can this table push at all? ──────────────────────────────────────
// A table only emits `postgres_changes` if it is in the `supabase_realtime`
// publication. Those are added one hand-applied migration at a time, so a table
// the app subscribes to may simply never push — and that is INDISTINGUISHABLE
// from a healthy socket unless you ask. `realtime_published_tables()` (migration
// 20260727130844) lets us ask. When the answer says a table we depend on is
// missing, the data layer polls fast instead of trusting a socket that will never
// speak. One call per session, cached.
let publishedPromise: Promise<Set<string> | null> | null = null;

export function realtimePublishedTables(): Promise<Set<string> | null> {
    if (!publishedPromise) {
        publishedPromise = (async () => {
            try {
                const { data, error } = await supabase.rpc('realtime_published_tables');
                // RPC not applied yet → null = "unknown". Callers treat unknown as
                // "assume it works" (the socket usually does) rather than as
                // "broken"; the poll is the safety net either way.
                if (error || !Array.isArray(data)) return null;
                return new Set(data as string[]);
            } catch { return null; }
        })();
    }
    return publishedPromise;
}

/** Forget the cached answer — after applying a migration, or on sign-out. */
export function resetRealtimePublishedCache(): void {
    publishedPromise = null;
}

/** Worst-case health across every open channel. No channels = 'live' (nothing to be down). */
export function realtimeHealth(): ChannelHealth {
    const all = Object.values(useLiveHealthStore.getState().channels);
    if (all.length === 0) return 'live';
    if (all.some((h) => h === 'down')) return 'down';
    if (all.every((h) => h === 'live')) return 'live';
    return 'connecting';
}

/** Reactive form of {@link realtimeHealth} for components. */
export function useRealtimeHealth(): ChannelHealth {
    return useLiveHealthStore((s) => {
        const all = Object.values(s.channels);
        if (all.length === 0) return 'live';
        if (all.some((h) => h === 'down')) return 'down';
        if (all.every((h) => h === 'live')) return 'live';
        return 'connecting';
    });
}

// ── The socket's auth token ──────────────────────────────────────────
// Realtime checks RLS with the JWT the socket was opened with. Access tokens
// expire (1 h by default) and `autoRefreshToken` renews the REST token silently —
// if the socket keeps the stale one, RLS-checked postgres_changes stop being
// delivered and the app goes quiet with a perfectly healthy-looking connection.
// supabase-js re-auths on TOKEN_REFRESHED itself; doing it here as well is
// idempotent and costs nothing, and it covers SIGNED_IN / SIGNED_OUT too.
let authWired = false;
function wireRealtimeAuth(): void {
    if (authWired) return;
    authWired = true;
    try {
        supabase.auth.onAuthStateChange((_event, session) => {
            try { supabase.realtime.setAuth(session?.access_token ?? null); } catch { /* older client */ }
        });
    } catch { /* auth unavailable — nothing to wire */ }
}

/** A join that has not answered in this long is down, and is retried. realtime-js
 *  times a join out at 10 s itself; this catches the case where nothing answers. */
const JOIN_WATCHDOG_MS = 15_000;

let seq = 0;
/** A short token unique for the life of the page. */
function nextSeq(): string {
    seq += 1;
    return seq.toString(36);
}

/** Take a channel off the realtime client's list without waiting for the server. */
function dropFromClient(ch: RealtimeChannel): void {
    try {
        (supabase.realtime as unknown as { _remove?: (c: RealtimeChannel) => void })._remove?.(ch);
    } catch { /* already gone */ }
}

interface LiveChannelOptions {
    /** Called after every successful (re)subscribe — refetch here, since events
     *  that happened while the socket was down were never delivered. */
    onResubscribe?: () => void;
}

/**
 * Subscribe to a Realtime channel that keeps itself alive.
 *
 * @param key    stable channel name, or null/'' to subscribe to nothing
 * @param build  attach the `.on(...)` bindings; called on every (re)connect
 */
export function useLiveChannel(
    key: string | null | undefined,
    build: (channel: RealtimeChannel) => void,
    options: LiveChannelOptions = {},
): void {
    const buildRef = useRef(build);
    const onResubscribeRef = useRef(options.onResubscribe);
    // Keep the latest handlers without tearing the socket down: writing the refs
    // in an effect (not during render) means a parent re-render with new callback
    // identities costs nothing, while a change of `key` still rebuilds properly.
    useEffect(() => {
        buildRef.current = build;
        onResubscribeRef.current = options.onResubscribe;
    });

    useEffect(() => {
        if (!key) return;
        wireRealtimeAuth();

        let disposed = false;
        let channel: RealtimeChannel | null = null;
        let attempt = 0;
        let everSubscribed = false;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;
        let joinTimer: ReturnType<typeof setTimeout> | null = null;

        // Health is kept per hook, not per name: two screens that listen under one
        // name must not write over each other's state (kplink).
        const healthKey = `${key}#${nextSeq()}`;
        const setHealth = (h: ChannelHealth) => useLiveHealthStore.getState().setHealth(healthKey, h);
        const clearWatchdog = () => { if (joinTimer) { clearTimeout(joinTimer); joinTimer = null; } };

        const teardown = () => {
            clearWatchdog();
            if (!channel) return;
            const dying = channel;
            channel = null;
            try {
                // removeChannel drops a channel from the client's list only once the
                // server confirms, which a dead socket never does, and a channel left
                // in that list is re-joined on the next reconnect with stale handlers.
                void supabase.removeChannel(dying).then(
                    (s) => { if (s !== 'ok') dropFromClient(dying); },
                    () => dropFromClient(dying),
                );
            } catch { dropFromClient(dying); }
        };

        const scheduleRetry = () => {
            if (disposed || retryTimer) return;
            // 1s, 2s, 4s, 8s, 16s, 30s… with jitter so many tabs don't stampede.
            const backoff = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
            attempt += 1;
            retryTimer = setTimeout(() => {
                retryTimer = null;
                teardown();
                open();
            }, backoff + Math.random() * 400);
        };

        const open = () => {
            if (disposed) return;
            setHealth('connecting');
            // ⛔ A FRESH topic on every open (kplink, 23 Sep 2026). `supabase.channel(name)`
            // hands back an EXISTING channel of that name, and a retry used to reopen
            // under the same name while the dead channel was still in the client's
            // list (see teardown). It got the dead channel back, and `subscribe()` on
            // a channel that is not 'closed' does nothing and never calls back: the
            // channel sat at 'connecting' with no timer, for good, and its screen
            // stopped hearing changes until a reload. The Karr Phone's status pill,
            // which reads this health, showed "Connecting" forever. For
            // postgres_changes the topic is only a name, so a suffix costs nothing.
            const mine = supabase.channel(`${key}~${nextSeq()}`);
            channel = mine;
            buildRef.current(mine);
            // Belt and braces: a join that never answers at all is treated as down.
            joinTimer = setTimeout(() => {
                joinTimer = null;
                if (disposed || channel !== mine) return;
                setHealth('down');
                scheduleRetry();
            }, JOIN_WATCHDOG_MS);
            mine.subscribe((status) => {
                // A channel already torn down still reports its CLOSED; that is not
                // news about the one open now, and must not schedule a retry of it.
                if (disposed || channel !== mine) return;
                if (status === 'SUBSCRIBED') {
                    clearWatchdog();
                    attempt = 0;
                    setHealth('live');
                    // Anything that changed while we were disconnected never
                    // arrived — the caller refetches so the gap can't persist.
                    if (everSubscribed) onResubscribeRef.current?.();
                    everSubscribed = true;
                    return;
                }
                if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                    setHealth('down');
                    scheduleRetry();
                }
            });
        };

        // Coming back to the tab or regaining the network: retry NOW, from the top
        // of the backoff. A slept laptop shouldn't wait out a 30-second timer.
        const wake = () => {
            if (disposed) return;
            if (useLiveHealthStore.getState().channels[healthKey] === 'live') return;
            // A join already on its way is left to finish; its watchdog has it.
            if (joinTimer) return;
            attempt = 0;
            if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
            teardown();
            open();
        };
        const onVisible = () => { if (document.visibilityState === 'visible') wake(); };
        window.addEventListener('online', wake);
        window.addEventListener('focus', wake);
        document.addEventListener('visibilitychange', onVisible);

        open();

        return () => {
            disposed = true;
            if (retryTimer) clearTimeout(retryTimer);
            window.removeEventListener('online', wake);
            window.removeEventListener('focus', wake);
            document.removeEventListener('visibilitychange', onVisible);
            teardown();
            useLiveHealthStore.getState().dropChannel(healthKey);
        };
    }, [key]);
}
